import React, { useCallback, useState } from 'react';
import { useField } from 'formik';

import { RequiredLabel } from '@/components/required-label';
import { toast } from '@/components/ui/toast';

import { createEmptySentimentTag, type SentimentTagFormValue } from '@/lib/sentiment';

import { PostCallPrompt } from './post-call-prompt';
import { SentimentTagDialog, type SentimentTagDraftErrors } from './sentiment-tag-dialog';
import { SentimentTagsList } from './sentiment-tags-list';

type SentimentPromptProps = {
  promptVariables: string[];
};

export const SentimentPrompt = React.memo(({ promptVariables }: SentimentPromptProps) => {
  const [tagsField, tagsMeta, tagsHelpers] = useField<SentimentTagFormValue[]>('sentiment_tags');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draft, setDraft] = useState<SentimentTagFormValue>(() => createEmptySentimentTag());
  const [draftErrors, setDraftErrors] = useState<SentimentTagDraftErrors>({});

  const tags = tagsField.value ?? [];
  const arrayError = typeof tagsMeta.error === 'string' ? tagsMeta.error : undefined;
  const isEditing = tags.some(tag => tag.id === draft.id);

  const openCreateDialog = useCallback(() => {
    setDraft(createEmptySentimentTag());
    setDraftErrors({});
    setIsDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((tag: SentimentTagFormValue) => {
    setDraft({ ...tag });
    setDraftErrors({});
    setIsDialogOpen(true);
  }, []);

  const removeTag = useCallback(
    (tagId: string) => {
      tagsHelpers.setValue(tags.filter(tag => tag.id !== tagId));
      toast.success('Tag deleted');
    },
    [tags, tagsHelpers]
  );

  const closeDialog = useCallback((open: boolean) => {
    setIsDialogOpen(open);

    if (!open) {
      setDraftErrors({});
    }
  }, []);

  const saveTag = useCallback(() => {
    const label = draft.label.trim();
    const nextErrors: SentimentTagDraftErrors = {};

    if (!label) {
      nextErrors.label = 'Label is required';
    } else if (
      tags.some(
        tag => tag.id !== draft.id && tag.label.trim().toLowerCase() === label.toLowerCase()
      )
    ) {
      nextErrors.label = 'Label must be unique';
    }

    if (Object.keys(nextErrors).length > 0) {
      setDraftErrors(nextErrors);
      return;
    }

    const nextTag: SentimentTagFormValue = {
      ...draft,
      label,
      description: draft.description.trim(),
    };
    const existingIndex = tags.findIndex(tag => tag.id === draft.id);

    if (existingIndex >= 0) {
      const nextTags = [...tags];
      nextTags[existingIndex] = nextTag;
      tagsHelpers.setValue(nextTags);
      toast.success('Tag updated');
    } else {
      tagsHelpers.setValue([...tags, nextTag]);
      toast.success('Tag added');
    }

    setDraftErrors({});
    setIsDialogOpen(false);
  }, [draft, tags, tagsHelpers]);

  return (
    <>
      <PostCallPrompt
        title="Sentiment prompt"
        description="Write the instruction used by the model to classify caller sentiment."
        name="sentiment_prompt"
        variablesName="sentiment_prompt_variables"
        promptVariables={promptVariables}
        inputId="sentiment-prompt"
        promptLabel={<RequiredLabel>Prompt</RequiredLabel>}
        promptPlaceholder="Write the instruction used to classify caller sentiment..."
        promptSectionClassName="border-b pb-6"
      >
        <SentimentTagsList
          tags={tags}
          arrayError={arrayError}
          onAddTag={openCreateDialog}
          onEditTag={openEditDialog}
          onRemoveTag={removeTag}
        />
      </PostCallPrompt>

      <SentimentTagDialog
        open={isDialogOpen}
        isEditing={isEditing}
        draft={draft}
        draftErrors={draftErrors}
        setDraft={setDraft}
        onOpenChange={closeDialog}
        onSave={saveTag}
      />
    </>
  );
});
